import { STATIC_SET_CONSTANTS, checkAnyUserType } from '@utils/Util';

export const PLANNING_STATUS = {
	COMPLETED: 'COMPLETED',
	PENDING: 'PENDING',
	SUBMITTED: 'SUBMITTED'
};

const CUTTING_TASK = 'cutting';

/**
 * Whether this user may move a task's due date once the TNA exists. The cutting date is what the
 * production plan is built around, so only an admin may move that one; every other task keeps the
 * rules it had. The server refuses the same change, so this only keeps the field from offering
 * what would be rejected.
 */
export const canEditTaskDueDate = (task) =>
	task?.stepDescription?.trim().toLowerCase() !== CUTTING_TASK || checkAnyUserType(['ADMIN']);

export const productionSteps = (orderCancelInfo) => {
	return [
		{ label: 'Order info', key: 'Order info', condition: true },
		{ label: 'PI', key: 'PI', condition: orderCancelInfo?.status !== 'APPROVED' },
		{ label: 'Supplier', key: 'Supplier', condition: orderCancelInfo?.status !== 'APPROVED' },
		{ label: 'Sales contract', key: 'Sales contract', condition: orderCancelInfo?.status !== 'APPROVED' },
		{ label: 'Material list', key: 'Material list', condition: orderCancelInfo?.status !== 'APPROVED' },
		{
			label: 'Commercial invoice',
			key: 'Commercial invoice',
			condition: orderCancelInfo?.status !== 'APPROVED'
		},
		{ label: 'Payment', key: 'Payment', condition: orderCancelInfo?.status !== 'APPROVED' }
	];
};

export const isProductOfSet = (product) => {
	return product?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
};

export const isPlaningCompleated = (item) => {
	return (
		item?.planningStatus === PLANNING_STATUS.COMPLETED ||
		item?.planningStatus === PLANNING_STATUS.SUBMITTED
	);
};

export const isPlaningPending = (item) => {
	return item?.planningStatus === PLANNING_STATUS.PENDING;
};

export const INITIAL_TASK_MODAL_STATE = {
	taskName: '',
	deliverableName: '',
	dueDate: ''
};

export const CREATE_TASK_MODAL_REQUIRED_FIELDS = {
	taskName: { required: true, label: 'Name' },
	deliverableName: { required: true, label: 'Deliverable name' },
	dueDate: { required: true, label: 'Task due date' }
};

export const GROUP_BY_TASK_LIST = 'deliverableId';
export const SUB_TASK_LABEL = 'subTask';

export const TNA_REQUEST_TYPE = {
	TEMPLATE: 'TEMPLATE',
	EXCEL_FILE: 'EXCEL_FILE'
};

export const isSetProduct = (style) => style?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
export const isPack = (style) => style?.setPackStyleFlag === STATIC_SET_CONSTANTS.PACK_PRODUCT;
export const isTnaCompleated = (style) => style?.planningStatus !== PLANNING_STATUS.PENDING;

export const tabFilterMap = {
	PENDING: 'PENDING',
	RUNNING: 'RUNNING',
	COMPLETED: 'COMPLETED',
	ORDER_DASHBOARD: 'ORDER_DASHBOARD',
	ON_SHIPMENT: 'ON_SHIPMENT',
	CANCEL: 'CANCEL'
};

// used in MyOrder.js
export const MY_ORDER_INITIAL_STATE = {
	projectList: [],
	page: 0,
	size: 15,
	loading: false,
	activeTab: 'ORDER_DASHBOARD',
	isPendingTab: false,
	filterBy: '',
	sort: ['RUNNING', 'PENDING', 'COMPLETED', 'ON_SHIPMENT', 'CANCEL'],
	project_type: ['BULK', 'DEVELOPMENT', 'SUPERVISION'],
	hasNext: false,
	height: window.innerHeight,
	userInfo: {},
	runningCount: 0,
	pendingCount: 0,
	completedCount: 0,
	cancelCount: 0,
	shipmentCount: 0,
	search: '',
	isSearching: false,
	totalElements: 0,
	showFilter: false,
	showSummary: true,
	queryParams: ''
};

export function getGroupPoData(poData) {
	const groupPoData = poData.reduce((acc, item) => {
		// Find if the productReferenceNumber group already exists
		let group = acc.find((g) => g.productReferenceNumber === item.productReferenceNumber);

		if (!group) {
			// Create a new group if it doesn't exist
			group = {
				productReferenceNumber: item.productReferenceNumber,
				name: item.name,
				poData: []
			};
			acc.push(group);
		}

		// Add the current item to the group
		group.poData.push({
			productId: item.productId,
			productReferenceNumber: item.productReferenceNumber,
			...item
		});

		return acc;
	}, []);
	return groupPoData;
}

export const incoTerms = [
	{ label: 'CIF', value: 'CIF', id: 'CIF' },
	{ label: 'FOB', value: 'FOB', id: 'FOB' },
	{ label: 'FCA', value: 'FCA', id: 'FCA' }
];

export const BUYER_ETD_UPDATE_REASON = [
	{ label: 'Fabrication Change due to Styling', value: 'Fabrication Change due to Styling' },
	{ label: 'Trims Change', value: 'Trims Change' },
	{ label: 'Fit Approval Delay', value: 'Fit Approval Delay' },
	{ label: 'Styling Change', value: 'Styling Change' },
	{ label: 'Delay to Release POs', value: 'Delay to Release POs' },
	{ label: 'Delay to Release Nominated Trims', value: 'Delay to Release Nominated Trims' },
	{
		label: 'PO or SKU Change Just Before the Shipment',
		value: 'PO or SKU Change Just Before the Shipment'
	},
	{ label: 'Shipment Booking Approval Delay', value: 'Shipment Booking Approval Delay' },
	{
		label: 'For Nominated Forwarder’s Specific Cut-Off',
		value: 'For Nominated Forwarder’s Specific Cut-Off'
	},
	{ label: 'Customer Pushback', value: 'Customer Pushback' },
	{ label: 'Text', value: 'Text' },
	{
		label: 'Others',
		value: 'Others'
	}
];

export const NITEX_ETD_UPDATE_REASON = [
	{
		label: 'Order placement more than Booked capacity',
		value: 'Order placement more than Booked capacity'
	},
	{
		label: 'Order placement with less lead time (agreed)',
		value: 'Order placement with less lead time (agreed)'
	},
	{ label: 'Due to Incomplete Tech pack', value: 'Due to Incomplete Tech pack' },
	{ label: 'Due to Wrong Tech Pack', value: 'Due to Wrong Tech Pack' },
	{ label: 'Fitting Comments Delay to Factory', value: 'Fitting Comments Delay to Factory' },
	{
		label: 'Due to improper or unclear Fitting Comments',
		value: 'Due to improper or unclear Fitting Comments'
	},
	{
		label: 'PP Sample Approval Delay',
		value: 'PP Sample Approval Delay'
	},
	{ label: 'Bulk Fabric Approval delay', value: 'Bulk Fabric Approval delay' },
	{
		label: 'Trims Approval Delay',
		value: 'Trims Approval Delay'
	},
	{
		label: 'Due to Production Permission Delay',
		value: 'Due to Production Permission Delay'
	},
	{
		label: 'Due to PO release Delay',
		value: 'Due to PO release Delay'
	},
	{
		label: 'Shipment Permission delay',
		value: 'Shipment Permission delay'
	},
	{
		label: 'Delay to Release Sales Contract',
		value: 'Delay to Release Sales Contract'
	},
	{
		label: 'Woven Fabric or Imported-Bulk Fabric Delay to In-house (Nitex Source)',
		value: 'Woven Fabric or Imported-Bulk Fabric Delay to In-house (Nitex Source)'
	},
	{
		label: 'Woven Fabric or Imported Fabric- Fabric Quality Issues (Nitex Source)',
		value: 'Woven Fabric or Imported Fabric- Fabric Quality Issues (Nitex Source)'
	},
	{
		label: 'Others',
		value: 'Others'
	}
];

export const FACTORY_ETD_UPDATE_REASON = [
	{
		label: 'Delay to Source Yarn',
		value: 'Delay to Source Yarn'
	},
	{
		label: 'Trims Submission Delay',
		value: 'Trims Submission Delay'
	},
	{ label: 'Fit Sample Submission Delay', value: 'Fit Sample Submission Delay' },
	{ label: 'PP Sample Submission Delay', value: 'PP Sample Submission Delay' },
	{
		label: 'Delay to complete Knitting within deadline',
		value: 'Delay to complete Knitting within deadline'
	},
	{
		label: 'Delay to complete Dyeing within deadline',
		value: 'Delay to complete Dyeing within deadline'
	},
	{
		label: 'Bulk Fabric Submission Delay',
		value: 'Bulk Fabric Submission Delay'
	},
	{ label: 'Bulk Fabric Quality Rejection', value: 'Bulk Fabric Quality Rejection' },
	{
		label: 'Trims Cards Submission Delay',
		value: 'Trims Cards Submission Delay'
	},
	{
		label: 'Incomplete Trims Cards Submission',
		value: 'Incomplete Trims Cards Submission'
	},
	{
		label: 'Size Set Submission Delay',
		value: 'Size Set Submission Delay'
	},
	{
		label: 'Pre-Production Meeting Delay',
		value: 'Pre-Production Meeting Delay'
	},
	{
		label: 'Delay to hit Production Cutting Date (PCD)',
		value: 'Delay to hit Production Cutting Date (PCD)'
	},
	{
		label: 'Failed to Hit Production Start Date',
		value: 'Failed to Hit Production Start Date'
	},
	{
		label: 'Production Inline Failed due to Quality Issues',
		value: 'Production Inline Failed due to Quality Issues'
	},
	{
		label: 'Pre-Final Inspection Failed due to Quality Issues',
		value: 'Pre-Final Inspection Failed due to Quality Issues'
	},
	{
		label: 'Final Inspection Failed Due to Quality Issues',
		value: 'Final Inspection Failed Due to Quality Issues'
	},
	{
		label: 'Due- Diligence Inspection (DDI) Failed Due to Quality Issues',
		value: 'Due- Diligence Inspection (DDI) Failed Due to Quality Issues'
	},
	{
		label: 'Failed to Hit Forwarder Cut-Off',
		value: 'Failed to Hit Forwarder Cut-Off'
	},
	{
		label: 'Others',
		value: 'Others'
	}
];

export const ETD_ENTRY_CORRECTION_TYPES = ['DUMMY_PO', 'SPLIT_ENTRY', 'WRONG_ENTRY'];

export const etdUpdateRequestedBy = [
	{ label: 'Buyer', value: 'BUYER' },
	{ label: 'Nitex', value: 'NITEX' },
	{ label: 'Factory', value: 'FACTORY' },
	{ label: 'Dummy PO', value: 'DUMMY_PO' },
	{ label: 'Split Entry', value: 'SPLIT_ENTRY' },
	{ label: 'Wrong Entry', value: 'WRONG_ENTRY' }
];
